/**
 * The hubs (the Navigation review, slice (c), approved by Arjun on 2026-10-06): pages that belong together joined under
 * one strip of tabs and one menu item. A hub holds no page of its own: each tab is today's page at today's address, so
 * every link, email and bookmark keeps working. Only some hubs have an address of their own (Catalog's /app/catalog),
 * which opens the tab the person last used there, or the first; another takes over its first tab's address (Devices,
 * /app/gateways), where a link opens that tab and the menu's item opens the last used.
 *
 * A hub shows when any of its tabs shows, and each tab keeps its page's own rule (rules.ts). Pure.
 */
import { gateOpen, type Gate, type MenuContext } from "./rules";

export type HubId =
  | "actions"
  | "reports"
  | "process"
  | "trace"
  | "capture"
  | "plant-model"
  | "devices"
  | "data-sources"
  | "catalog";

/** The width a page's PageContainer gives it (6xl by default), so the strip of tabs over it lines up with its title. */
export type PageWidth = "xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl";

/** A tab of a hub: a page at its own address. */
export interface HubTab extends Gate {
  /** Its name in the person's remembered tab: never changed once shipped. */
  id: string;
  label: string;
  to: string;
  /** The tab is its address alone, never the pages under it (the Process graph's /app/twin). */
  end?: boolean;
  /** Its page's width, where it is not PageContainer's default 6xl. */
  width?: PageWidth;
}

export interface Hub {
  id: HubId;
  label: string;
  /** The hub's own address: a link to it opens a tab (hubEntryOf). */
  to: string;
  tabs: readonly HubTab[];
}

/**
 * Actions: what the plant does about its scrap (Arjun, 2026-10-10): the action list (every recommendation once, with what
 * the plant did with it), the improvement projects started from it where the org has them on, and the changes the
 * plant logged in its own words. It takes over the action list's address; a project's own page is under its tab.
 */
export const ACTIONS: Hub = {
  id: "actions",
  label: "Actions",
  to: "/app/twin/actions",
  tabs: [
    { id: "actions", label: "Actions", to: "/app/twin/actions", foundry: true, width: "5xl" },
    { id: "projects", label: "Improvement projects", to: "/app/twin/improvement-projects", foundry: true, when: "projects", width: "5xl" },
    { id: "logged", label: "Logged changes", to: "/app/twin/logged-changes", foundry: true, width: "5xl" },
  ],
};

/**
 * Reports: the plant's reports, the past weekly digests and the studies, at the Reports address. A study is analysis,
 * not a summary (the consultant's deeper look at a month, or one a Sahas analyst asks for; every cited figure
 * recomputed by the server, every claim accepted or struck by a reviewer before the plant sees it), so it keeps a tab
 * of its own beside the briefs (D5, Arjun 2026-10-06). The digest is retired (D5): the weekly brief is the plant's
 * weekly record, and the digests published before it stay readable under Past digests. Each tab shows by its page's
 * own rule. A report, a digest and a study keep their own addresses under their tab.
 */
export const REPORTS: Hub = {
  id: "reports",
  label: "Reports",
  to: "/app/twin/reports",
  tabs: [
    { id: "reports", label: "Reports", to: "/app/twin/reports", foundry: true, width: "7xl" },
    { id: "digests", label: "Past digests", to: "/app/twin/digests", foundry: true, when: "digests", width: "4xl" },
    { id: "studies", label: "Studies", to: "/app/twin/studies", foundry: true, when: "studies", width: "4xl" },
  ],
};

/**
 * Process: the plant's limits (process windows and scrap targets) first, then the correlations and what the plant has
 * learnt (the findings), at /app/twin/process. Knowledge shows where its findings are open to the viewer.
 */
export const PROCESS: Hub = {
  id: "process",
  label: "Process",
  to: "/app/twin/process",
  tabs: [
    { id: "limits", label: "Limits", to: "/app/twin/limits", foundry: true, width: "5xl" },
    { id: "correlations", label: "Correlations", to: "/app/twin/correlations", foundry: true, width: "7xl" },
    { id: "knowledge", label: "Knowledge", to: "/app/twin/knowledge", foundry: true, when: "knowledgeOpen" },
  ],
};

/**
 * Traceability: a part's or a lot's trace, and the heats and casting lots, opened on Parts, where the trace box is.
 */
export const TRACE: Hub = {
  id: "trace",
  label: "Traceability",
  to: "/app/twin/trace",
  tabs: [
    { id: "parts", label: "Parts", to: "/app/twin/parts", foundry: true, width: "7xl" },
    { id: "batches", label: "Batches", to: "/app/twin/batches", foundry: true, width: "7xl" },
  ],
};

/**
 * Floor capture: what operators enter every shift (readings and registers, rejects) and the station view of one
 * machine, at /app/capture. It opens on the registers; a person who may not capture sees the station view alone. The
 * Station tab is the machine picker, and each machine's view sits under it. Rejections (D2 of the 2026-10-27 CWC
 * follow-ups) is the reject form and the plant's defect list; it was Reject capture, and keeps that tab's id, so a
 * person's remembered tab still opens it.
 */
export const CAPTURE: Hub = {
  id: "capture",
  label: "Floor capture",
  to: "/app/capture",
  tabs: [
    { id: "entry", label: "Data entry", to: "/app/data-entry", foundry: true, perm: "capture.write", width: "3xl" },
    { id: "rejects", label: "Rejections", to: "/app/twin/rejections", foundry: true, perm: "capture.write", width: "4xl" },
    { id: "station", label: "Station", to: "/app/twin/station", foundry: true },
  ],
};

/**
 * Plant model: the process graph, each equipment and its bindings, the stations (named groups of its equipment, with
 * the people who work them: the station workspaces plan, N3), and building a plant, at the Process graph's address.
 * Every member reads the stations, as the server lets them; only an org admin changes them, on the page itself.
 * Building a plant changes the model, so its tab is a model writer's, as the graph's own Build button is.
 */
export const PLANT_MODEL: Hub = {
  id: "plant-model",
  label: "Plant model",
  to: "/app/twin",
  tabs: [
    { id: "graph", label: "Process graph", to: "/app/twin", end: true, foundry: true, width: "7xl" },
    { id: "equipment", label: "Equipment", to: "/app/twin/equipment", width: "7xl" },
    { id: "stations", label: "Stations", to: "/app/twin/stations", foundry: true },
    { id: "build", label: "Build a plant", to: "/app/twin/build", foundry: true, perm: "model.write" },
  ],
};

/** Devices: the gateways a plant's data comes through, and connecting a new device. It takes over the Gateways address. */
export const DEVICES: Hub = {
  id: "devices",
  label: "Devices",
  to: "/app/gateways",
  tabs: [
    { id: "gateways", label: "Gateways", to: "/app/gateways", width: "7xl" },
    { id: "connect", label: "Connect a device", to: "/app/connect" },
  ],
};

/**
 * Data sources: where a plant's data comes in (Acquisition, docs/plans/2026-10-11_acquisition.md, P2d). Files is the
 * page Data sources always was, at its address: the files sent for import, the readers, the plant agents and the
 * import runs. Acquisition is a plant's tag list or data export studied into what each point is, where it belongs,
 * whether to keep it and how often to read it, which a person reviews and applies; a study's own page is under its
 * tab. Files matches its own address only (end), so Acquisition's pages are never its. It takes over the Files
 * address, as Devices takes over Gateways'. Every member sees both tabs; only an org admin uploads, decides and applies,
 * on the pages themselves.
 */
export const DATA_SOURCES: Hub = {
  id: "data-sources",
  label: "Data sources",
  to: "/app/twin/data-sources",
  tabs: [
    { id: "files", label: "Files", to: "/app/twin/data-sources", end: true, foundry: true },
    { id: "acquisition", label: "Acquisition", to: "/app/twin/data-sources/acquisition", foundry: true, width: "7xl" },
  ],
};

/** Catalog: what a plant makes and pours, set up once and changed now and then. */
export const CATALOG: Hub = {
  id: "catalog",
  label: "Catalog",
  to: "/app/catalog",
  tabs: [
    { id: "products", label: "Products and tooling", to: "/app/twin/patterns", foundry: true, width: "5xl" },
    { id: "recipes", label: "Recipes", to: "/app/twin/recipes", foundry: true, width: "5xl" },
    { id: "materials", label: "Materials", to: "/app/twin/materials", width: "5xl" },
  ],
};

export const HUBS: Readonly<Record<HubId, Hub>> = {
  actions: ACTIONS,
  reports: REPORTS,
  process: PROCESS,
  trace: TRACE,
  capture: CAPTURE,
  "plant-model": PLANT_MODEL,
  devices: DEVICES,
  "data-sources": DATA_SOURCES,
  catalog: CATALOG,
};

/** Every address a hub holds: its own and its tabs', each once. */
export const HUB_PATHS: readonly string[] = [
  ...new Set(Object.values(HUBS).flatMap((h) => [h.to, ...h.tabs.map((t) => t.to)])),
];

/** The tabs of a hub its viewer sees, in the hub's order. */
export function hubTabs(hub: Hub, ctx: MenuContext): HubTab[] {
  return hub.tabs.filter((t) => gateOpen(t, ctx));
}

/** Whether a page at `pathname` is at this address or under it (a station's /app/twin/station/:id). */
export function atOrUnder(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** Whether a page is a tab's: at its address, or under it where the tab is not its address alone (`end`). */
export function tabMatches(t: Pick<HubTab, "to" | "end">, pathname: string): boolean {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return t.end ? path === t.to : atOrUnder(path, t.to);
}

/** The tab of a hub a page is. */
export function hubTabAt(hub: Hub, pathname: string): HubTab | undefined {
  return hub.tabs.find((t) => tabMatches(t, pathname));
}

/**
 * The address a hub opens at: the tab the person last used there, where they still see it, else the hub's first tab
 * they see; null where they see none.
 */
export function hubEntryOf(tabs: readonly Pick<HubTab, "id" | "to">[], remembered: string | null): string | null {
  return (tabs.find((t) => t.id === remembered) ?? tabs[0])?.to ?? null;
}

/**
 * Where a hub's own address goes (#1217 review, LOW 1): nowhere yet while the org's packs are unread (a foundry tab
 * would read as hidden), for at most the landing's wait; then the hub's entry tab with the address's query, or /app
 * where its viewer sees no tab. After the wait, it goes by the tabs known so far.
 */
export function hubIndexTarget(a: {
  packsKnown: boolean;
  timedOut: boolean;
  tabs: readonly Pick<HubTab, "id" | "to">[];
  remembered: string | null;
  search: string;
}): string | null {
  if (!a.packsKnown && !a.timedOut) return null;
  const to = hubEntryOf(a.tabs, a.remembered);
  return to ? `${to}${a.search}` : "/app";
}

/** The reads that decide a tab beyond the packs and the rights (a Gate's `when`), each true while it is on its way. */
export type GateReads = Partial<Record<NonNullable<Gate["when"]>, boolean>>;

/**
 * Whether a hub's strip still waits (#1224 review, LOW 2): until the org's packs are read, and until each read a tab's
 * own rule rests on has answered (Reports' digest and study lists, Process's findings), so the strip never grows a tab
 * after it is drawn.
 */
export function hubPending(hub: Hub, packsKnown: boolean, reads: GateReads): boolean {
  return !packsKnown || hub.tabs.some((t) => t.when !== undefined && reads[t.when] === true);
}

